import React, { useCallback, useContext, useState, useEffect, useMemo } from 'react';
import { css } from "styled-components/macro"; //eslint-disable-line
import {Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton,
  InputLabel, List, ListItem, ListItemSecondaryAction, ListItemText, MenuItem,
  Select, TextField, Typography, Pagination} from '@mui/material';
import {Campaign, PushPin, PushPinOutlined, Delete, Edit} from '@mui/icons-material';
import { fetchMessages, createMessage, updateMessage, deleteMessage} from "helpers/messages.js";
import { fetchUsers } from "helpers/userManagement.js";
import AuthContext from 'contexts/AuthContext';
import { SectionHeading } from "components/misc/Headings";

const CATEGORY_OPTIONS = ["low", "normal", "high", "urgent"];
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'urgent': return 'error';
    case 'high': return 'warning';
    case 'normal': return 'info';
    case 'low': return 'default';
    default: return 'default';
  }
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function Posts() {
  const {role, designation} = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [pgParams, setPgParams] = useState({
    page: 0,
    pageSize: 10
  });
  const [pgInfo, setPgInfo] = useState({limit: 10, offset: 0,
    total: 0, pages: 0});
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // A Treasurer-designated EXECUTIVE has handed Messages off to a Secretary -
  // an undesignated or President-designated EXECUTIVE keeps full access.
  const hasPermission = useMemo(() =>
    role === "ADMIN" || (role === "EXECUTIVE" && designation !== "TREASURER"), [role, designation]);
  const canView = useMemo(() => role === "ADMIN" || role === "EXECUTIVE" || role === "MEMBER", [role]);
  const isAdmin = useMemo(() => role === "ADMIN", [role]);

  // Separate pinned and recent messages
  const pinnedMessages = useMemo(() =>
    messages.filter(msg => msg.is_pinned).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [messages]
  );

  const recentMessages = useMemo(() =>
    messages.filter(msg => !msg.is_pinned).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [messages]
  );

  const handleAddMessage = () => {
    const newMessage = {
      title: "",
      content: "",
      priority: "normal",
      is_pinned: false,
    };
    setSelectedMessage(newMessage);
    setEditMode(true);
    setIsDialogOpen(true);
  };

  const handleEditMessage = (message) => {
    setSelectedMessage({...message});
    setEditMode(true);
    setIsDialogOpen(true);
  };

  // Handle page change
  const handlePageChange = useCallback((event, newPage) => {
    setPgParams(prev => ({ ...prev, page: newPage - 1 }));
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((event) => {
    const newPageSize = parseInt(event.target.value);
    setPgParams(prev => ({ ...prev, pageSize: newPageSize, page: 0 }));
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchMessages(pgParams.page + 1, pgParams.pageSize, setMessages, setPgInfo);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  }, [pgParams.page, pgParams.pageSize]);

  const refreshMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Reset to first page and refresh
      setPgParams(prev => ({ ...prev, page: 0 }));
      await fetchMessages(1, pgParams.pageSize, setMessages, setPgInfo);
    } catch (err) {
      console.error('Error refreshing messages:', err);
      setError('Failed to refresh messages. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  }, [pgParams.pageSize]);

  const handleTogglePin = async (message) => {
    if (!hasPermission) return;

    try {
      const updatedMessage = { ...message, is_pinned: !message.is_pinned };
      await updateMessage(message.id, updatedMessage);
      await refreshMessages();
    } catch (err) {
      console.error('Error toggling pin:', err);
      setError('Failed to update message pin status.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!isAdmin) return;

    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await deleteMessage(messageId);
        await refreshMessages();
      } catch (err) {
        console.error('Error deleting message:', err);
        setError('Failed to delete message.');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!selectedMessage?.title?.trim() || !selectedMessage?.content?.trim()) {
      setError('Title and content are required.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setSaving(true);
    try {
      const request = {
        title: selectedMessage.title,
        content: selectedMessage.content,
        priority: selectedMessage.priority,
        is_pinned: selectedMessage.is_pinned,
      };

      if (selectedMessage.id) {
        await updateMessage(selectedMessage.id, request);
      } else {
        // Only notify on a brand-new broadcast, not on every edit of an existing one.
        try {
          const users = await fetchUsers();
          request.recipient_emails = (Array.isArray(users) ? users : [])
            .filter(u => u.role === 'MEMBER' && u.status === 'ACTIVE' && u.email)
            .map(u => u.email);
        } catch (fetchErr) {
          console.error('Error fetching members to notify:', fetchErr);
          request.recipient_emails = [];
        }
        await createMessage(request);
      }

      await refreshMessages();
      setIsDialogOpen(false);
      setSelectedMessage(null);
      setEditMode(false);
    } catch (err) {
      console.error('Error saving message:', err);
      setError('Failed to save message. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  }, [selectedMessage, refreshMessages]);

  const handleCancel = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedMessage(null);
    setEditMode(false);
    setError(null);
  }, []);

  const handleInputChange = (field, value) => {
    setSelectedMessage(prev => ({ ...prev, [field]: value }));
  };

  // Load messages when pagination params change
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Early return for permission check
  if (!canView) {
    return null;
  }

  const MessageItem = ({ message, showActions = true }) => (
    <ListItem divider>
      <ListItemText sx={{pr: 10}}
        primary={
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" component="span" sx={{
              fontSize: { xs: '1rem', sm: '1.25rem' },
              wordBreak: 'break-word'
            }}>
              {message.title}
            </Typography>
            <Chip
              label={message.priority}
              size="small"
              color={getPriorityColor(message.priority)}
              variant="outlined"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            />
            <IconButton
              edge="end"
              onClick={() => handleTogglePin(message)}
              size="small"
              sx={{ minWidth: 'auto', p: { xs: 0.5, sm: 1 } }}
            >
              {message.is_pinned ?
                <PushPin color="primary" fontSize="small" /> :
                <PushPinOutlined color="primary" fontSize="small" />
              }
            </IconButton>
          </Box>
        }
        secondary={
          <Box display="flex" alignItems="left" flexDirection="column">
            <span style={{
              marginBottom: 15,
              fontSize: window.innerWidth < 600 ? 14 : 16,
              wordBreak: 'break-word',
              lineHeight: 1.4
            }}>
              {message.content}
            </span>
            <span style={{
              fontSize: window.innerWidth < 600 ? 11 : 12,
              color: '#666'
            }}>
              {message.updated_at && formatDate(message.updated_at)}
            </span>
          </Box>
        }
      />
      {showActions && hasPermission && (
        <ListItemSecondaryAction sx={{ right: { xs: 8, sm: 16 } }}>
          <Box display="flex" gap={0.5} flexDirection={{ xs: 'column', sm: 'row' }}>
            <IconButton
              edge="end"
              onClick={() => handleEditMessage(message)}
              title="Edit message"
              size="small"
              sx={{ minWidth: 'auto', p: { xs: 0.5, sm: 1 } }}
            >
              <Edit fontSize="small" />
            </IconButton>
            {isAdmin && (
              <IconButton
                edge="end"
                onClick={() => handleDeleteMessage(message.id)}
                title="Delete message"
                size="small"
                color="error"
                sx={{ minWidth: 'auto', p: { xs: 0.5, sm: 1 } }}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );

  return (<>
    {/* Message Edit/Create Dialog */}
    <Dialog open={isDialogOpen} onClose={handleCancel} maxWidth="md" fullWidth fullScreen={window.innerWidth < 600}
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 1, sm: 2 },
          maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' }
        }
      }}
    >
      <DialogTitle sx={{
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 }
      }}>
        {editMode && selectedMessage?.id ? 'Edit Message' : 'Create New Message'}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Title"
            value={selectedMessage?.title || ''}
            onChange={(e) => handleInputChange('title', e.target.value)}
            fullWidth
            required
            size={window.innerWidth < 600 ? 'small' : 'medium'}
          />
          <TextField
            label="Content"
            value={selectedMessage?.content || ''}
            onChange={(e) => handleInputChange('content', e.target.value)}
            multiline
            rows={window.innerWidth < 600 ? 3 : 4}
            fullWidth
            required
            size={window.innerWidth < 600 ? 'small' : 'medium'}
          />
          <FormControl fullWidth size={window.innerWidth < 600 ? 'small' : 'medium'}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={selectedMessage?.priority || 'normal'}
              onChange={(e) => handleInputChange('priority', e.target.value)}
              label="Priority"
            >
              {CATEGORY_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedMessage?.is_pinned || false}
                onChange={(e) => handleInputChange('is_pinned', e.target.checked)}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
              />
            }
            label={
              <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Pin this message
              </Typography>
            }
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        gap: 1
      }}>
        <Button
          onClick={handleCancel}
          disabled={saving}
          size={window.innerWidth < 600 ? 'small' : 'medium'}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !selectedMessage?.title?.trim() || !selectedMessage?.content?.trim()}
          size={window.innerWidth < 600 ? 'small' : 'medium'}
        >
          {saving ? 'Saving...' : (selectedMessage?.id ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
    <div style={{padding: window.innerWidth < 600 ? 0 : 15,
      margin: window.innerWidth < 600 ? '30px 0px' : '40px 30px' }}>
      <Box mb={3} display="flex" borderBottom="1px solid #0000001f" pb={1}
        justifyContent="space-between" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }} >
        <SectionHeading sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Messages
        </SectionHeading>
        {hasPermission && (
          <Button color="success"
            style={{maxWidth: 'fit-content', margin: window.innerWidth < 600 ? "10px auto" : "15px 0px"}}
            variant="contained"
            startIcon={<Campaign />}
            onClick={handleAddMessage}
            disabled={loading}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            sx={{
              minWidth: { xs: 'auto', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.875rem' }
            }}
          >
            {window.innerWidth < 600 ? 'New Message' : 'Broadcast New Message'}
          </Button>
        )}
      </Box>

      <Box display="flex" justifyContent="space-between" gap={3} width="100%"
        flexDirection={{ xs: 'column', lg: 'row' }}
        sx={{ marginRight: { xs: 0, lg: 7.5 } }} >
        {/* Pinned Messages Column */}
        <div style={{width: window.innerWidth < 900 ? '100%' : 'calc(max(48%, 300px))'}}>
          <Typography variant="h5" gutterBottom color="primary"
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              mb: { xs: 1, sm: 2 }
            }}
          >
            📌 Pinned Messages
          </Typography>
          <List sx={{'& .MuiListItem-root': {px: { xs: 1, sm: 2 },py: { xs: 1, sm: 1.5 }}}}>
            {pinnedMessages.length > 0 ? (
              pinnedMessages.map((message) => (
                <MessageItem key={message.id} message={message} />
              ))
            ) : (
              <ListItem>
                <ListItemText secondary="No pinned messages"
                  sx={{
                    '& .MuiListItemText-secondary': {
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
              </ListItem>
            )}
          </List>
        </div>

        {/* Recent Messages Column */}
        <div style={{width: window.innerWidth < 900 ? '100%' : 'calc(max(48%, 300px))'}}>
          <Typography variant="h5" gutterBottom color="primary"
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: { xs: 1, sm: 2 } }}>
            📝 Recent Messages
          </Typography>
          <List sx={{'& .MuiListItem-root': {px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 1.5 }}}}>
            {recentMessages.length > 0 ? (
              recentMessages.map((message) => (
                <MessageItem key={message.id} message={message} />
              ))
            ) : (
              <ListItem>
                <ListItemText
                  secondary="No recent messages"
                  sx={{
                    '& .MuiListItemText-secondary': {
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
              </ListItem>
            )}
          </List>
          {/* Pagination Controls - Bottom Only */}
          {pgInfo?.pages > 1 &&
            <Box mt={3} ml="auto" display="flex" justifyContent="space-between"
              alignItems="center" flexWrap="wrap" gap={2}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              <Box display="flex" alignItems="center" gap={2} flexDirection="row"
                width={{ xs: '100%', sm: 'auto' }} >
                <Typography variant="body2" color="textSecondary"
                  sx={{fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    textAlign: { xs: 'center', sm: 'left' }}} >
                  Showing {(pgInfo.offset || 0) + 1}-{Math.min((pgInfo.offset || 0) + (pgInfo.limit || 0), pgInfo.total || 0)} of {pgInfo.total || 0} messages
                </Typography>
                <FormControl size="small" style={{width: 100}} >
                  <InputLabel>Per page</InputLabel>
                  <Select
                    value={pgParams.pageSize}
                    onChange={handlePageSizeChange}
                    label="Per page"
                    disabled={loading}
                  >
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <MenuItem key={size} value={size}>{size}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Pagination count={pgInfo.pages || 0} page={pgParams.page + 1}
                onChange={handlePageChange} disabled={loading} color="primary"
                showFirstButton={window.innerWidth >= 600}
                showLastButton={window.innerWidth >= 600}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                sx={{
                  '& .MuiPagination-ul': {
                    justifyContent: 'center'
                  }
                }}
              />
            </Box>
          }
        </div>
      </Box>


      {error && (
        <Alert
          severity="error"
          style={{marginTop: 20}}
          onClose={() => setError(null)}
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
            '& .MuiAlert-message': {
              wordBreak: 'break-word'
            }
          }}
        >
          {error}
        </Alert>
      )}
    </div>
  </>);
}
